/**
 * Create a PrivacyLens account (email + password).
 *
 * Pre-fills the email from the `?email=` query param so the Pro
 * delivery-redirect ("Create an account with this email to keep your docs")
 * lands the user one click from signup.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Loader2, ArrowRight } from "lucide-react";
import { apiClient } from "../lib/api";
import { startLogin } from "../lib/auth";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Signup failed. Please try again.";
}

const passwordIssues = (pw) => {
  const list = [];
  if (pw.length < 8) list.push("at least 8 characters");
  if (!/[0-9]/.test(pw)) list.push("a number");
  if (!/[\W_]/.test(pw)) list.push("a symbol");
  return list;
};

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillEmail = params.get("email") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [emailDelivered, setEmailDelivered] = useState(true);
  const [resendBusy, setResendBusy] = useState(false);

  const pwIssues = passwordIssues(password);
  const mismatch = password && confirm && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pwIssues.length) {
      setErr("Password must include " + pwIssues.join(", ") + ".");
      return;
    }
    if (mismatch) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const r = await apiClient.post(
        "/auth/signup",
        { email, password, name: name || undefined },
        { withCredentials: true },
      );
      if (r.data?.verified) {
        // Existing Google user accepted — but with the new account-takeover
        // guard this no longer happens. Keep the branch as safety net.
        navigate("/login", {
          state: { email, returnTo: "/dashboard" },
        });
      } else {
        setEmailDelivered(r.data?.email_delivery_ok !== false);
        setDone(true);
      }
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResendBusy(true);
    try {
      await apiClient.post("/auth/resend-verification", { email });
      // Re-render: success message stays generic — we don't leak whether
      // delivery actually worked this time, but we tell the user we tried.
      setEmailDelivered(true);
    } catch {
      /* swallow — anti-enumeration response is intentional */
    } finally {
      setResendBusy(false);
    }
  };

  if (done) {
    return (
      <main className="border-b border-black min-h-[80vh]" data-testid="signup-done">
        <section className="max-w-md mx-auto px-6 lg:px-8 py-16 text-center">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
            / {emailDelivered ? "Check your inbox" : "Account created"}
          </div>
          <h1 className="font-display text-4xl tracking-tight leading-[1.05] mb-4">
            {emailDelivered
              ? "Verify your email."
              : "We couldn't email you."}
          </h1>
          {emailDelivered ? (
            <p className="text-[#475569] text-sm mb-8 leading-relaxed">
              We&apos;ve sent a verification link to{" "}
              <code className="font-mono text-black">{email}</code>. Click the
              link to activate your account and access your dashboard.
            </p>
          ) : (
            <div
              className="border border-[#F59E0B] bg-[#FFF7ED] p-4 mb-6 text-left"
              data-testid="signup-delivery-failed"
            >
              <p className="text-sm text-[#0A0A0A] leading-relaxed mb-3">
                Your account was created with{" "}
                <code className="font-mono">{email}</code>, but our email
                provider rejected the message just now.
              </p>
              <p className="text-xs text-[#475569] leading-relaxed">
                This can happen if our sender domain is paused, or in preview
                mode where only the project owner&apos;s address is reachable.
                Click below to retry — or just go to the login page and use{" "}
                <strong>&ldquo;Forgot password?&rdquo;</strong> to set a fresh
                verification.
              </p>
            </div>
          )}

          {!emailDelivered && (
            <button
              type="button"
              onClick={resend}
              disabled={resendBusy}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A0A0A] text-white text-sm hover:bg-[#0047FF] mb-3 disabled:opacity-60"
              data-testid="signup-retry-email"
            >
              {resendBusy && <Loader2 className="w-4 h-4 animate-spin" />}
              {resendBusy ? "Retrying…" : "Try sending again"}
            </button>
          )}

          <p className="text-xs text-[#94A3B8] mb-6">
            Didn&apos;t get it? Check spam, or use the &ldquo;resend&rdquo; link on the
            login page after a minute.
          </p>
          <Link
            to="/login"
            state={{ email, verifyPending: true }}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-black bg-white text-sm hover:bg-[#F1F5F9]"
            data-testid="signup-go-to-login"
          >
            Go to login <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="border-b border-black min-h-[80vh]" data-testid="signup-page">
      <section className="max-w-md mx-auto px-6 lg:px-8 py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
          / Sign up
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-2">
          Create your account.
        </h1>
        <p className="text-[#475569] text-sm mb-8">
          Track scans, manage subscriptions, and access your purchased Pro
          documents in one dashboard.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Name (optional)
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <UserIcon className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="Jane Doe"
                data-testid="signup-name-input"
                autoComplete="name"
              />
            </div>
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Email
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Mail className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="you@company.com"
                data-testid="signup-email-input"
                autoComplete="email"
              />
            </div>
            {prefillEmail && (
              <p className="text-[10px] text-[#475569] mt-1.5">
                Pre-filled from your Pro purchase. Use this same email so we
                can link your existing scan to the account.
              </p>
            )}
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Password
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Lock className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="At least 8 chars, 1 number, 1 symbol"
                data-testid="signup-password-input"
                autoComplete="new-password"
              />
            </div>
            {password && pwIssues.length > 0 && (
              <p
                className="text-[10px] text-[#B45309] mt-1.5"
                data-testid="signup-password-hint"
              >
                Add: {pwIssues.join(", ")}.
              </p>
            )}
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Confirm password
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Lock className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="Repeat your password"
                data-testid="signup-confirm-input"
                autoComplete="new-password"
              />
            </div>
            {mismatch && (
              <p className="text-[10px] text-[#B91C1C] mt-1.5">
                Passwords do not match.
              </p>
            )}
          </label>

          {err && (
            <p
              className="text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2"
              data-testid="signup-error"
            >
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#0A0A0A] text-white py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#0047FF] transition-colors"
            data-testid="signup-submit-button"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Creating account…" : "Create account"}
          </button>

          <div className="text-xs pt-1 text-[#475569]">
            Already have an account?{" "}
            <Link to="/login" className="underline hover:text-[#0047FF]">
              Sign in
            </Link>
          </div>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-[#E2E8F0]" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
            Or
          </span>
          <div className="flex-1 h-px bg-[#E2E8F0]" />
        </div>

        <button
          type="button"
          onClick={() => startLogin("/dashboard")}
          className="w-full border border-black bg-white py-3 inline-flex items-center justify-center gap-2 hover:bg-[#F1F5F9]"
          data-testid="signup-google-button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
            />
            <path
              fill="#FBBC05"
              d="M5.85 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.67-2.83Z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.67 2.83C6.71 7.31 9.14 5.38 12 5.38Z"
            />
          </svg>
          Continue with Google
        </button>

        <p className="text-[10px] text-center text-[#94A3B8] mt-6 leading-relaxed">
          By creating an account you accept our{" "}
          <a
            href="/legal/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms
          </a>{" "}
          &amp;{" "}
          <a
            href="/legal/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>
          .
        </p>
      </section>
    </main>
  );
}
